import { Trans, useLingui } from '@lingui/react/macro';
import { Loader2, Upload, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useCreateProject } from '@/api/hooks';
import { Button } from '@/components/ui/button';
import { Card, CardAction, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import type { DrillSide } from '@/types/api';
import { apiErrorMessage } from '@/utils/apiError';
import { Dropzone } from './Dropzone';
import type { DirStats } from './ProgramFilesTabs';
import { DRILL_SIDES, ProgramFilesTabs } from './ProgramFilesTabs';

interface ProgramEntry {
  id: string;
  file: File;
  drillSide: DrillSide;
}

interface CreateProjectModalProps {
  trigger: React.ReactElement;
}

/**
 * Modal dialog to create a new project by uploading model files and .H programs.
 */
export const CreateProjectModal = ({ trigger }: CreateProjectModalProps): React.ReactElement => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [targetFile, setTargetFile] = useState<File | null>(null);
  const [blankFile, setBlankFile] = useState<File | null>(null);
  const [programEntries, setProgramEntries] = useState<ProgramEntry[]>([]);
  const [activeDir, setActiveDir] = useState<DrillSide>('+Z');

  const handleOpenChange = (newOpen: boolean): void => {
    setOpen(newOpen);
    if (newOpen) {
      setTargetFile(null);
      setBlankFile(null);
      setProgramEntries([]);
      setActiveDir('+Z');
    }
  };
  const { t } = useLingui();

  const createProjectMutation = useCreateProject();

  const handleProgramDrop = (files: File[]): void => {
    const newEntries: ProgramEntry[] = files.map((file) => ({
      id: `${file.name}-${Date.now()}-${Math.random()}`,
      file,
      drillSide: activeDir,
    }));
    setProgramEntries((prev) => [...prev, ...newEntries]);
  };

  const removeProgramEntry = (id: string): void => {
    setProgramEntries((prev) => prev.filter((entry) => entry.id !== id));
  };

  const dirStats = useMemo((): Record<DrillSide, DirStats> => {
    return Object.fromEntries(
      DRILL_SIDES.map((dir) => [dir, { fileCount: programEntries.filter((e) => e.drillSide === dir).length }])
    ) as Record<DrillSide, DirStats>;
  }, [programEntries]);

  const activeDirEntries = programEntries.filter((e) => e.drillSide === activeDir);

  const canSubmit = targetFile !== null;

  const handleSubmit = (): void => {
    if (!targetFile) return;
    createProjectMutation.mutate(
      {
        targetModel: targetFile,
        blankModel: blankFile ?? undefined,
        programFiles: programEntries.map(({ file, drillSide }) => ({ file, drillSide })),
      },
      {
        onSuccess: (project) => {
          setOpen(false);
          navigate(`/projects/${project.id}`);
        },
        onError: (e) => {
          toast.error(apiErrorMessage(e, t`Failed to create project.`));
        },
      }
    );
  };

  const loading = createProjectMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger render={trigger} />
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            <Trans>Create new project</Trans>
          </DialogTitle>
        </DialogHeader>

        <div>
          <div className="max-h-[80vh] overflow-y-auto -mx-4 p-4 flex flex-col gap-6">
            {/* Target model drop zone */}
            <div>
              <div className="mb-1.5 text-sm font-semibold uppercase tracking-wider text-slate-800">
                <Trans>Target 3D model</Trans>
              </div>
              {targetFile ? (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm text-slate-800">{targetFile.name}</CardTitle>
                    <CardAction>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        title={t`Remove`}
                        className="hover:text-red-600"
                        onClick={() => setTargetFile(null)}
                      >
                        <X />
                      </Button>
                    </CardAction>
                  </CardHeader>
                </Card>
              ) : (
                <Dropzone
                  accept={{ 'application/step': ['.stp', '.step'] }}
                  onDrop={([file]) => setTargetFile(file)}
                  hint=".stp, .step"
                />
              )}
            </div>

            {/* Blank model drop zone */}
            <div>
              <div className="mb-1.5 text-sm font-semibold uppercase tracking-wider text-slate-800">
                <Trans>Blank model</Trans>
              </div>
              <p className="mb-2 text-xs text-slate-500">
                <Trans>Optional — if left empty, a solid box the size of the target model is used.</Trans>
              </p>
              {blankFile ? (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm text-slate-800">{blankFile.name}</CardTitle>
                    <CardAction>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        title={t`Remove`}
                        className="hover:text-red-600"
                        onClick={() => setBlankFile(null)}
                      >
                        <X />
                      </Button>
                    </CardAction>
                  </CardHeader>
                </Card>
              ) : (
                <Dropzone
                  accept={{ 'application/step': ['.stp', '.step'] }}
                  onDrop={([file]) => setBlankFile(file)}
                  hint=".stp, .step"
                />
              )}
            </div>

            {/* .H files tabs */}
            <div>
              <div className="mb-2 text-sm font-semibold uppercase tracking-wider text-slate-800">
                <Trans>.H source files</Trans>
              </div>
              <ProgramFilesTabs
                activeDir={activeDir}
                onDirChange={setActiveDir}
                dirStats={dirStats}
                onDrop={handleProgramDrop}
              >
                {activeDirEntries.length !== 0 && (
                  <table className="w-full text-sm mt-2">
                    <tbody>
                      {activeDirEntries.map((entry) => (
                        <tr key={entry.id} className="hover:bg-slate-50">
                          <td className="px-4 py-2 font-mono text-sm text-slate-800">{entry.file.name}</td>
                          <td className="px-4 py-2 w-0">
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="hover:text-red-600"
                              onClick={() => removeProgramEntry(entry.id)}
                              title={t`Remove`}
                            >
                              <X />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </ProgramFilesTabs>
            </div>
          </div>

          <DialogFooter>
            <DialogClose render={<Button variant="outline" disabled={loading} />}>
              <X data-icon="inline-start" /> <Trans>Cancel</Trans>
            </DialogClose>
            <Button type="button" onClick={handleSubmit} disabled={!canSubmit || loading}>
              {loading ? (
                <>
                  <Loader2 data-icon="inline-start" className="animate-spin" /> <Trans>Loading…</Trans>
                </>
              ) : (
                <>
                  <Upload data-icon="inline-start" /> <Trans>Upload & create</Trans>
                </>
              )}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};
